'use client';

import { useState } from 'react';
import { Copy, Share2 } from 'lucide-react';
import { toast } from 'sonner';

type HoroscopeShareActionsProps = {
  title: string;
  urlPath: string;
  summary: string;
  className?: string;
};

export default function HoroscopeShareActions({
  title,
  urlPath,
  summary,
  className = '',
}: HoroscopeShareActionsProps) {
  const [sharing, setSharing] = useState(false);

  const fullUrl =
    typeof window === 'undefined' ? urlPath : `${window.location.origin}${urlPath}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      toast.success('Link copied');
    } catch {
      toast.error('Could not copy the link');
    }
  };

  const handleShare = async () => {
    if (!navigator.share) {
      await handleCopy();
      return;
    }

    setSharing(true);
    try {
      await navigator.share({
        title,
        text: summary,
        url: fullUrl,
      });
    } catch {
      // Ignore user-cancelled share sheet exits.
    } finally {
      setSharing(false);
    }
  };

  return (
    <div className={`flex flex-wrap gap-3 ${className}`}>
      <button
        type="button"
        onClick={() => void handleShare()}
        className="inline-flex items-center gap-2 rounded-xl border border-stardust-gold/16 bg-stardust-gold/10 px-4 py-2.5 text-sm font-medium text-stardust-gold transition-colors hover:bg-stardust-gold/16"
      >
        <Share2 size={16} />
        {sharing ? 'Sharing...' : 'Share this reading'}
      </button>
      <button
        type="button"
        onClick={() => void handleCopy()}
        className="inline-flex items-center gap-2 rounded-xl border border-starlight/12 bg-white/5 px-4 py-2.5 text-sm font-medium text-starlight transition-colors hover:bg-white/10"
      >
        <Copy size={16} />
        Copy link
      </button>
    </div>
  );
}
